import { LABEL_SETTINGS } from "../../../../../Constants/LabelSettings"

export type EEGMontages = {
	"Longitudinal-bipolar": string
	"Bipolar-Transverse": string
	"SD-Detection": string
	Recorded: string
	"Create Montage": string
}

export const EEG_LOW_FILTERS = { "0.1Hz": 0.1, "0.5Hz": 0.5, "1Hz": 1.0, "2Hz": 2.0, "3Hz": 3.0, Off: 0 }

export const EEG_HIGH_FILTERS = { Off: 0, "100Hz": 100.0, "90Hz": 90.0, "80Hz": 80.0, "70Hz": 70.0, "60Hz": 60.0, "50Hz": 50.0, "40Hz": 40.0, "30Hz": 30.0 }

export const EEG_NOTCH_FILTERS = { "60Hz": 60.0, "50Hz": 50.0, Off: 0 }

// Values are in V/mm (volts per millimeter of trace deflection).
// Based on MCP-SRS-15-REQ-FUNC-010
export const EEG_SENSITIVITY = {
	"0.5 uV/mm": 0.5 * 1e-6,
	"0.75 uV/mm": 0.75 * 1e-6,
	"1 uV/mm": 1 * 1e-6,
	"1.5 uV/mm": 1.5 * 1e-6,
	"2 uV/mm": 2 * 1e-6,
	"3 uV/mm": 3 * 1e-6,
	"4 uV/mm": 4 * 1e-6,
	"5 uV/mm": 5 * 1e-6,
	"7 uV/mm": 7 * 1e-6,
	"10 uV/mm": 10 * 1e-6,
	"15 uV/mm": 15 * 1e-6,
	"20 uV/mm": 20 * 1e-6,
	"30 uV/mm": 30 * 1e-6,
	"40 uV/mm": 40 * 1e-6,
	"50 uV/mm": 50 * 1e-6,
	"75 uV/mm": 75 * 1e-6,
	"100 uV/mm": 100 * 1e-6,
	"150 uV/mm": 150 * 1e-6,
	"200 uV/mm": 200 * 1e-6,
	"300 uV/mm": 300 * 1e-6,
	"400 uV/mm": 400 * 1e-6,
	"500 uV/mm": 500 * 1e-6,
	"750 uV/mm": 750 * 1e-6,
	"1 mV/mm": 1 * 1e-3,
	"1.5 mV/mm": 1.5 * 1e-3,
	"2 mV/mm": 2 * 1e-3,
	"3 mV/mm": 3 * 1e-3,
	"4 mV/mm": 4 * 1e-3,
	"5 mV/mm": 5 * 1e-3,
	"7.5 mV/mm": 7.5 * 1e-3,
	"10 mV/mm": 10 * 1e-3,
	"20 mV/mm": 20 * 1e-3,
	"50 mV/mm": 50 * 1e-3,
	"100 mV/mm": 100 * 1e-3,
	"200 mV/mm": 200 * 1e-3,
	"500 mV/mm": 500 * 1e-3,
}

// Values are in V/mm (volts per millimeter of trace deflection).
export const ECG_SENSITIVITY = {
	"1 mm/mV": 1e-3,
	"2 mm/mV": 0.5e-3,
	"3 mm/mV": 1e-3 / 3,
	"7 mm/mV": 1e-3 / 7,
	"10 mm/mV": 0.1e-3,
}

export const PERSYST_EEG_SENSITIVITY = {
	"1000 uV": 1000,
	"700 uV": 700,
	"500 uV": 500,
	"300 uV": 300,
	"200 uV": 200,
	"150 uV": 150,
	"100 uV": 100,
	"70 uV": 70,
	"50 uV": 50,
	"30 uV": 30,
	"20 uV": 20,
	"15 uV": 15,
	"10 uV": 10,
	"7 uV": 7,
	"5 uV": 5,
	"3 uV": 3,
	"2 uV": 2,
	"1 uV": 1,
}

const EEG_AVERAGE_REF = "AVG"
const EXCLUDED_LEADS = new Set([EEG_AVERAGE_REF, "Ref"])

const isPhysicalLead = (lead: string): boolean => !EXCLUDED_LEADS.has(lead)

export const isResolvableEEGLead = (lead: string, montageMapping: Map<string, string>) => {
	return lead === EEG_AVERAGE_REF || montageMapping.has(lead)
}

export type EEGChannelDisplayInfo = {
	key: string
	label: string
	leads: string[]
	prefixedLeads: string[]
	type: "eeg" | "ecg"
	spacer?: boolean
}

export class EEGDisplayConfig {
	public LFF: number
	public HFF: number
	public notch: number
	public sensitivityMicroVolts: number

	private montage: EEGMontage | null
	private montageMapping: Map<string, string> // from the physical inputs on the amp, to the lead names


	constructor(montage: EEGMontage | null, LFF = 1.0, HFF = 30.0, notch = 60.0, sensitivityMicroVolts = 1.0, montageMapping=new Map()) {
		this.montage = montage
		this.LFF = LFF
		this.HFF = HFF
		this.notch = notch
		this.sensitivityMicroVolts = sensitivityMicroVolts
		this.montageMapping = montageMapping
	}

	// For display purposes, replace the channel pair with an optional alias.
	get channelLabels(): string[] {
        return this.channelDisplayInfo.map(({ label }) => label)
	}

	// The strings that uniquely identify each channel from a data perspective.
	get channelDataKeys(): string[] {
        return this.channelDisplayInfo.map(({ key }) => key)
	}

	get leads(): string[] {
		const leads = this.channelDisplayInfo.flatMap(({ prefixedLeads }) => prefixedLeads)
		return [...new Set(leads)]
	}

	get dataChannelDisplayInfo(): EEGChannelDisplayInfo[] {
		return this.channelDisplayInfo.filter(ch => !ch.spacer)
	}

	get channelPairs(): string[][] {
		return this.dataChannelDisplayInfo.map(({ leads }) => leads)
	}

	get eegModality(): string | undefined {
		const eegChannels = this.dataChannelDisplayInfo
			.filter(channel => channel.type === "eeg")
			.map(channel => channel.key)

		if (eegChannels.length > 0) {
			return `Montage=${eegChannels.join(",")}:${this.qualifiers}`
		}

		return undefined
	}

	get ecgModalities(): string[] {
		return this.dataChannelDisplayInfo
			.filter(channel => channel.type === "ecg")
			.map(channel => channel.key)
	}

	get channelDisplayInfo(): EEGChannelDisplayInfo[] {
		const channelDisplayInfo: EEGChannelDisplayInfo[] = []
		let spacerIndex = 0

		for (const { first, second, alias, type, ecgBasename, spacer } of this.validChannels()) {
			if (spacer) {
				const key = `SPACER-${spacerIndex}`
				spacerIndex += 1

				channelDisplayInfo.push({
					key,
					label: "",
					leads: [] as string[],
					prefixedLeads: [],
					type: "eeg" as const,
					spacer: true,
				})
				continue
			}

			const channelType = type ?? "eeg"

			if (channelType === "ecg") {
				const basename = ecgBasename ?? "ECG_II"

				channelDisplayInfo.push({
					key: basename,
					label: LABEL_SETTINGS[basename]?.name ?? basename.replace("_", " "),
					leads: [],
					prefixedLeads: [basename],
					type: channelType,
				})
				continue
			}

			const mappedFirst = this.resolveLead(first)
			const mappedSecond = this.resolveLead(second)
			const key = `${mappedFirst}-${mappedSecond}`

			const prefixedLeads = [mappedFirst, mappedSecond]
				.filter(isPhysicalLead)
				.map(lead => `EEG_${lead}`)

			channelDisplayInfo.push({
				key,
				label: alias ?? key,
				leads: [mappedFirst, mappedSecond],
				prefixedLeads,
				type: channelType,
			})
		}

		return channelDisplayInfo
	}

	get qualifiers() {
		return "Notch=" + this.notch + "+HFF=" + this.HFF + "+LFF=" + this.LFF
	}

	private validChannels = () => {
		const montageChannels = this.montage?.channels ?? []
		return montageChannels.filter(({ first, second, type, spacer }) => {
			if (spacer || (type ?? "eeg") === "ecg") {
				return true
			}

			return (
				isResolvableEEGLead(first, this.montageMapping) &&
				isResolvableEEGLead(second, this.montageMapping)
			)
		})
	}

	private resolveLead = (lead: string): string => {
		if (lead === EEG_AVERAGE_REF) {
			return lead
		}

		const resolved = this.montageMapping.get(lead)

		if (resolved === undefined) {
			throw new Error(`Unable to resolve EEG lead: ${lead}`)
		}

		return resolved
	}
}

export type EEGChannel = {
	id?: string
	first: string
	second: string
	external: boolean
	alias?: string
	type?: "eeg" | "ecg"
	ecgBasename?: string
	spacer?: boolean
}

export type EEGMontage = {
	id: number
	name: string
	type: MontageType
	userId: number | null
	channels: EEGChannel[]
}

export const montageHasECGChannel = (montage?: EEGMontage | null): boolean => (
	montage?.channels.some(channel => channel.type === "ecg") ?? false
)

export enum MontageType {
	PERSONAL = "personal", // Only visible to the user who owns it. Fully editable.
	SHARED = "shared", // Visible to all users in the workspace. Editable with appropriate permissions.
	STANDARD = "standard" // Visible to all users. Non-editable.
}
